import { canParseJson, getTokenHeader } from "@/helpers/commonHelper";
import axios from "axios";
import router from "@/router";
const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

const COST_MANAGEMENT_PRINCIPAL_ID = "00000000-0000-0000-1000-000000000001";
const COST_MANAGEMENT_PATH = "/llms/cost-management";

export default {
  async getGlobalEoConfig({ commit }) {
    try {
      const headers = getTokenHeader();
      const response = await axios.get(
        `${env.auth_api_client_url}globalConfig`,
        { headers }
      );
      commit("SET_GLOBAL_EO_CONFIG", response.data?.data);
    } catch (e) {
      throw new Error(`API Error: ${e}`);
    }
  },
  async getAllUsers({ commit, state }, filter) {
    if (!filter) filter = { limit: 1000 };
    if (!state.currentUser) await this.dispatch("getCurrentUser");
    const isAdmin = state.currentUser?.scopes.includes("role:admin");
    if (isAdmin) await this.dispatch("getPrincipals");
    const headers = getTokenHeader();
    if (headers)
      try {
        const response = await axios.get(
          `${env.auth_api_client_url}users/find`,
          {
            params: { ...filter },
            headers
          }
        );
        const users = response?.data?.data;
        users?.forEach(user => {
          user.name = `${user.first_name} ${user.last_name}`;
          user.isAdmin = user.additional_data?.eberl_admin;
          if (isAdmin)
            user.principal = state.principals?.find(
              p => p.id === user.principal_id
            );
        });
        commit("SET_ALL_USERS", users);
      } catch (ex) {
        throw new Error(`API Error: ${ex}`);
      }
    else throw new Error(`API Error: Headers missing`);
  },
  async getGroups({ commit, state }, filter) {
    if (!filter) filter = { limit: 1000 };
    await this.dispatch("getPrincipals");
    const headers = getTokenHeader();
    if (headers)
      try {
        const response = await axios.get(`${env.auth_api_client_url}groups`, {
          params: { ...filter },
          headers
        });
        const groups = response?.data?.data;
        groups.forEach(group => {
          group.principal = state.principals?.find(
            p => p.id === group.principal_id
          );
        });
        commit("SET_GROUPS", groups);
      } catch (ex) {
        throw new Error(`API Error: ${ex}`);
      }
    else throw new Error(`API Error: Headers missing`);
  },
  async getOAuthClients({ commit }, filter) {
    const headers = getTokenHeader();
    if (headers)
      try {
        const response = await axios.get(`${env.auth_api_client_url}clients`, {
          params: {
            ...filter
          },
          headers
        });
        commit("SET_OAUTH_CLIENTS", response?.data?.data);
      } catch (ex) {
        throw new Error(`API Error: ${ex}`);
      }
    else throw new Error(`API Error: Headers missing`);
  },
  async getThirdPartySystems({ commit }, filter) {
    const headers = getTokenHeader();
    if (headers)
      try {
        const response = await axios.get(
          `${env.auth_api_client_url}thirdPartySystems`,
          {
            params: {
              ...filter
            },
            headers
          }
        );
        commit("SET_THIRD_PARTY_SYSTEMS", response?.data?.data);
      } catch (ex) {
        throw new Error(`API Error: ${ex}`);
      }
    else throw new Error(`API Error: Headers missing`);
  },
  async getRoleCategories({ commit, state }) {
    const headers = getTokenHeader();
    if (headers) {
      const response = await axios.get(
        `${env.auth_api_client_url}roleCategories/with_roles`,
        {
          params: {
            currentPrincipal: state.selectedPrincipal.id
          },
          headers
        }
      );
      commit("SET_ROLE_CATEGORIES", response?.data?.data ?? []);
    }
  },
  async getNavbarItems({ commit, state }) {
    try {
      let modules;
      if (!state.currentUser) await this.dispatch("getCurrentUser");
      if (
        state.currentUser?.regions?.length &&
        state.currentUser?.principal_id ===
          "00000000-0000-0000-0000-100000000000"
      ) {
        modules = "regionEdit";
        if (router.currentRoute?.path !== "/poi-region-edit")
          await router.push("/poi-region-edit");
      } else {
        const headers = getTokenHeader(),
          result = await axios.get(`${env.auth_api_client_url}modules/mine`, {
            headers
          });
        modules = result.data?.data ?? [];
        modules.forEach(module => {
          module.selected = false;
          module.hovered = false;
          module.sub_items?.forEach(sub => {
            sub.selected = false;
            sub.hovered = false;
          });
        });
        const selectedPrincipal = state.selectedPrincipal;
        if (selectedPrincipal) {
          if (!state.currentUser) await this.dispatch("getCurrentUser");
          const isAdmin = state.currentUser?.scopes?.includes("role:admin");
          let userRoles = [];
          let userRoleIds = [];
          state.currentUser?.roles?.forEach(role => {
            userRoles.push(role.id);
            userRoleIds.push(role._id);
          });
          userRoles.push(state.currentUser.principal_id);
          modules = modules.filter(
            module =>
              !!selectedPrincipal.modules.find(
                selectedPrincipalModule =>
                  selectedPrincipalModule._id === module._id ||
                  selectedPrincipalModule === module._id
              ) ||
              (isAdmin && module.id === "admin")
          );
          modules = modules.filter(
            module =>
              module.roles.some(role => userRoleIds.includes(role)) || isAdmin
          );
          modules.forEach(module => {
            if (module?.sub_items?.length) {
              module.sub_items = module.sub_items.filter(sub =>
                sub.roles.some(
                  role =>
                    userRoles?.includes(role) || userRoles?.includes("admin")
                )
              );
            }
          });

          // Keep Cost Management limited to the dedicated OYA principal.
          const canSeeCostManagement =
            selectedPrincipal.id === COST_MANAGEMENT_PRINCIPAL_ID;
          if (!canSeeCostManagement) {
            modules = modules
              .map(module => {
                if (module.path === COST_MANAGEMENT_PATH) return null;
                if (module?.sub_items?.length) {
                  module.sub_items = module.sub_items.filter(
                    sub => sub.path !== COST_MANAGEMENT_PATH
                  );
                }
                return module;
              })
              .filter(Boolean);
          }

          // Normalize icon key so sidebar uses the dedicated Cost Management icon.
          modules = modules.map(module => {
            if (module.path === COST_MANAGEMENT_PATH) {
              module.icon = "$costManagement";
            }
            if (module?.sub_items?.length) {
              module.sub_items = module.sub_items.map(sub => {
                if (sub.path === COST_MANAGEMENT_PATH) {
                  sub.icon = "$costManagement";
                }
                return sub;
              });
            }
            return module;
          });
        }
      }
      commit("SET_MODULES", modules);
    } catch (ex) {
      throw new Error(`API Error: ${ex}`);
    }
  },
  async getPrincipals({ commit }, filter) {
    if (!filter) filter = {};
    const headers = getTokenHeader();
    if (headers)
      try {
        const result = await axios.get(
          `${env.auth_api_client_url}principals/`,
          {
            params: { ...filter },
            headers
          }
        );
        commit("SET_PRINCIPALS", result?.data?.data);
      } catch (ex) {
        throw new Error(`API Error: ${ex}`);
      }
    else throw new Error("Not Authorised");
  },
  async getPrincipalList({ commit }, filter) {
    if (!filter) filter = {};
    const headers = getTokenHeader();
    if (headers)
      try {
        const result = await axios.get(
          `${env.auth_api_client_url}principals/`,
          {
            params: { ...filter },
            headers
          }
        );
        commit("SET_PRINCIPAL_LIST", result?.data?.data);
      } catch (ex) {
        throw new Error(`API Error: ${ex}`);
      }
    else throw new Error("Not Authorised");
  },
  async getPrincipalById({ commit }, id) {
    const headers = getTokenHeader();
    if (headers)
      try {
        const result = await axios.get(
          `${env.auth_api_client_url}principals/get/${id}`,
          {
            headers
          }
        );
        commit("SET_SELECTED_PRINCIPAL", result?.data?.data);
      } catch (ex) {
        throw new Error(`API Error: ${ex}`);
      }
    else throw new Error("Not Authorised");
  },
  async getCurrentUser({ commit }) {
    const userString = localStorage.getItem("user"),
      sessionString = localStorage.getItem("session");
    if (canParseJson(sessionString) && canParseJson(userString)) {
      const session = JSON.parse(sessionString),
        headers = getTokenHeader(),
        userId = session.user_id;
      let user = JSON.parse(userString);
      if (user) {
        if (!user?.scopes?.includes("role:admin"))
          await this.dispatch("getPrincipalById", user.principal_id);

        commit("SET_CURRENT_USER", user);
      } else if (!user && headers && userId) {
        try {
          const response = await axios.get(
            `${env.auth_api_client_url}users/find`,
            {
              params: { id: userId },
              headers
            }
          );
          const user = response?.data?.data?.[0];
          if (!user?.scopes?.includes("role:admin"))
            await this.dispatch("getPrincipalById", user.principal_id);

          commit("SET_CURRENT_USER", user);
          return user?.data?.data?.[0];
        } catch (ex) {
          throw new Error(`API Error: ${ex}`);
        }
      } else throw new Error("Not Authorised");
    } else return false;
  },
  async getModules({ commit }) {
    const sessionString = localStorage.getItem("session");
    if (canParseJson(sessionString)) {
      const headers = getTokenHeader();
      const { scopes } = JSON.parse(sessionString);
      if (scopes.length) {
        if (scopes?.includes("role:admin")) {
          try {
            const response = await axios.get(
              `${env.auth_api_client_url}modules`,
              {
                headers
              }
            );
            commit("SET_ALL_MODULES", response.data?.data);
          } catch (e) {
            console.error(e);
          }
        }
      }
    }
  }
};
